import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useNavState } from "@/navigation/useNavState";
import { resultsHref } from "@/navigation/trail";
import { ARCHIVE_TOTAL } from "@/data/catalogue";
import { toggleMode } from "@/styles/theme";
import { PinnedBar } from "@/components/PinnedBar";
import { HouseIcon } from "@/assets/icons/HouseIcon";
import { ArrowIcon } from "@/assets/icons/ArrowIcon";
import { LoginIcon } from "@/assets/icons/LoginIcon";
import { SearchIcon } from "@/assets/icons/SearchIcon";
import { useSaved } from "@/data/useSaved";

function measureIndicator(
  nav: HTMLElement,
  el: HTMLElement,
): React.CSSProperties {
  const navRect = nav.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  return { left: elRect.left - navRect.left, width: elRect.width };
}

const TABS = [
  { id: "esplora", label: "Esplora", spark: true },
  { id: "viewer", label: "Visualizzatore 3D", spark: false },
  { id: "prototipia", label: "Prototipia", spark: false },
  { id: "qualita", label: "Qualità / CQ", spark: false },
  { id: "revisione", label: "Revisione", spark: false },
] as const;

export type TabId = (typeof TABS)[number]["id"];

interface TopPanelProps {
  mode: "results" | "detail";
  onExit?: (to: string) => void;
}

export function TopPanel({ mode, onExit }: TopPanelProps) {
  const navigate = useNavigate();
  const state = useNavState();
  const { count } = useSaved();
  const [queryDraft, setQueryDraft] = useState(state.query);
  const [settled, setSettled] = useState(false);

  // Suppress re-animation after first mount
  useEffect(() => {
    const t = setTimeout(() => setSettled(true), 1400);
    return () => clearTimeout(t);
  }, []);

  // Keep query draft in sync when navigating back to results
  useEffect(() => {
    if (mode === "results") setQueryDraft(state.query);
  }, [state.query, mode]);

  const go = (to: string) => (onExit ? onExit(to) : navigate(to));
  const runSearch = () =>
    go(resultsHref({ trail: [], query: queryDraft.trim() }));

  const { pathname } = useLocation();
  const onSaved = pathname === "/saved";

  const tabsNavRef = useRef<HTMLElement>(null);
  const tabsRefs = useRef<Array<HTMLAnchorElement | HTMLButtonElement | null>>(
    [],
  );
  const activeTabIndex = onSaved ? 1 : 0;
  const [tabIndicatorStyle, setTabIndicatorStyle] =
    useState<React.CSSProperties>({});

  useEffect(() => {
    const nav = tabsNavRef.current;
    const el = tabsRefs.current[activeTabIndex];
    if (!nav || !el) return;
    const update = () => setTabIndicatorStyle(measureIndicator(nav, el));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [activeTabIndex]);

  return (
    <div className={`panel-top${settled ? " settled" : ""}`}>
      <div className="appbar">
        <div className="appleft">
          <button
            className="home"
            onClick={() =>
              mode === "detail" ? go(resultsHref(state)) : go("/")
            }
            aria-label={mode === "detail" ? "Torna alla ricerca" : "Home"}
            {...(mode === "detail" ? { "data-detail": true } : {})}
          >
            <span className="home-icon home-icon--house">
              <HouseIcon />
            </span>
            <span className="home-icon home-icon--back">
              <ArrowIcon size={20} />
            </span>
          </button>
          <nav className="tabs" ref={tabsNavRef}>
            <button
              ref={(el) => {
                tabsRefs.current[0] = el;
              }}
              className={onSaved ? undefined : "active"}
              onClick={() => go(resultsHref(state))}
            >
              Ricerca
            </button>
            <Link
              to="/saved"
              className={`tab-saved${onSaved ? " active" : ""}`}
              ref={(el) => {
                tabsRefs.current[1] = el;
              }}
            >
              Salvati {count > 0 && <span className="tab-badge">{count}</span>}
            </Link>
            <span className="tab-indicator" style={tabIndicatorStyle} />
          </nav>
        </div>
        <div className="appbar-search">
          <div className="searchbox">
            <SearchIcon />
            <input
              type="text"
              value={queryDraft}
              onChange={(e) => setQueryDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runSearch();
              }}
              placeholder="descrivi il pezzo che cerchi…"
              aria-label="Ricerca nell'archivio asset 3D"
            />
          </div>
          <button className="pill solid" type="button" onClick={runSearch}>
            Cerca
          </button>
          <div className="toolmeta">
            <span>
              RESULTS: <b>— / {ARCHIVE_TOTAL.toLocaleString("it-IT")}</b>
            </span>
          </div>
        </div>
        <div className="appright">
          <span className="user">M. Conti · Prototipia</span>
          <button className="iconbtn" onClick={() => go("/")} aria-label="Esci">
            <LoginIcon />
          </button>
          <span className="kebab">⋮</span>
          <button
            className="mode-btn"
            type="button"
            onClick={() => toggleMode()}
            aria-label="Cambia tema"
          >
            &#9680;
          </button>
        </div>
      </div>

      <PinnedBar state={state} />
    </div>
  );
}

// ── Swappable strip ──────────────────────────────────────────────────────────

interface SwappableStripProps {
  mode: "results" | "detail";
  tab: TabId;
  onTabChange: (t: TabId) => void;
  activeRef?: string;
  exiting?: boolean;
}

export function SwappableStrip({
  mode,
  tab,
  onTabChange,
  activeRef,
  exiting,
}: SwappableStripProps) {
  // Reset to esplora when the asset changes
  useEffect(() => {
    if (mode === "detail") onTabChange("esplora");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRef]);

  const navRef = useRef<HTMLElement>(null);
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const nav = navRef.current;
    const activeIdx = TABS.findIndex((t) => t.id === tab);
    const el = btnRefs.current[activeIdx];
    if (!nav || !el) return;
    const update = () => setIndicatorStyle(measureIndicator(nav, el));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [tab]);

  if (mode === "results") {
    return null;
  }

  return (
    <nav ref={navRef} className={`ftabs${exiting ? " strip-exit" : ""}`}>
      {TABS.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => {
            btnRefs.current[i] = el;
          }}
          className={`ftab${tab === t.id ? " active" : ""}`}
          data-label={t.spark ? `✦ ${t.label}` : t.label}
          type="button"
          onClick={() => onTabChange(t.id)}
        >
          {t.spark && <span className="spark">✦</span>} {t.label}
        </button>
      ))}
      <span className="tab-indicator" style={indicatorStyle} />
    </nav>
  );
}
